/**
 * Opt-in, on-device diagnostic for "the on-screen keyboard covers the chat
 * input" (installed PWA, Android Chrome).
 *
 * COMPLETELY inert unless enabled: no listeners, no observers, no timers and
 * no storage writes. Enable with `?kbdebug=1` (persisted in localStorage),
 * disable with `?kbdebug=0` (clears the flag and the stored log).
 *
 * When enabled it keeps a ring buffer of viewport/focus/layout events, each
 * with a snapshot of the numbers that decide whether the input is covered,
 * and persists it (throttled, and on pagehide) so the log survives a reload
 * or the app being killed.
 *
 * Privacy: only geometry, event kinds, tag names and a sanitized data-testid
 * (or role) are recorded. Never text content, input values, ids, classes or
 * attribute values other than the allow-listed testid shape. Nothing leaves
 * the device: there is no network call here.
 */
import { isEditableElement } from '@/lib/keyboardOpen';
import {
  getActiveKeyboardMode,
  getStoredKeyboardMode,
  type KeyboardMode,
} from '@/lib/keyboardMode';

export const KBDEBUG_STORAGE_KEY = 'andi-kbdebug';
export const KBDEBUG_LOG_STORAGE_KEY = 'andi-kbdebug-log';
export const KBDEBUG_PARAM = 'kbdebug';
export const MAX_ENTRIES = 300;
export const PERSIST_INTERVAL_MS = 2000;
const SCROLL_COALESCE_MS = 100;
const OBSERVER_RESCAN_MS = 2000;
const PREV_KINDS = 5;
/** A reported edge within this many px of the visual viewport bottom is visible. */
const COVER_TOLERANCE_PX = 2;

export const COMPOSER_SELECTOR = '[data-testid="chat-composer"]';
const SHELL_SELECTOR = '[data-testid="app-shell"]';

export interface Snapshot {
  /** window.innerHeight */
  ih: number;
  /** visualViewport.height */
  vvh: number | null;
  vvTop: number | null;
  vvPageTop: number | null;
  vvScale: number | null;
  /** document.scrollingElement scrollTop / scrollHeight */
  scrollTop: number | null;
  scrollH: number | null;
  compTop: number | null;
  compBottom: number | null;
  /** Tag name (+ sanitized testid or role) of the focused editable, if any. */
  focus: string | null;
  focusTop: number | null;
  focusBottom: number | null;
  shellH: number | null;
  standalone: boolean;
  /** Focused editable (or the composer when nothing is focused) is under the keyboard. */
  inputCovered: boolean;
}

export interface LogEntry {
  /** Epoch ms. */
  t: number;
  kind: string;
  /** Tag/testid/size text only, never content. */
  detail?: string;
  snap: Snapshot;
  /** Set when inputCovered changed on this entry. */
  flip?: 'covered' | 'uncovered';
  /** Kinds of the previous events, oldest first, present on a flip. */
  prev?: string[];
}

const round = (n: number): number => Math.round(n * 10) / 10;

/** inputCovered rule: element bottom below the visible bottom edge of the visual viewport. */
export function computeInputCovered(
  rectBottom: number | null,
  viewport: { offsetTop: number; height: number } | null,
  fallbackHeight: number = typeof window === 'undefined' ? 0 : window.innerHeight
): boolean {
  if (rectBottom === null) return false;
  const visibleBottom = viewport ? viewport.offsetTop + viewport.height : fallbackHeight;
  return rectBottom > visibleBottom - COVER_TOLERANCE_PX;
}

/**
 * A short, non-identifying label for an element: tag name plus an
 * allow-listed data-testid (lowercase words and dashes only, so anything
 * carrying an id or number is dropped) or, failing that, its role.
 */
export function describeElement(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const testId = el.getAttribute('data-testid');
  if (testId && /^[a-z][a-z-]{0,39}$/.test(testId)) return `${tag}[${testId}]`;
  const role = el.getAttribute('role');
  if (role && /^[a-z]{1,20}$/.test(role)) return `${tag}[role=${role}]`;
  return tag;
}

function isStandalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

function readBottom(el: Element | null): { top: number; bottom: number } | null {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return { top: round(rect.top), bottom: round(rect.bottom) };
}

/** Collects the numbers that decide whether the input is covered. */
export function takeSnapshot(): Snapshot {
  const viewport = window.visualViewport;
  const scroller = document.scrollingElement;
  const composer = readBottom(document.querySelector(COMPOSER_SELECTOR));
  const active = document.activeElement;
  const focused = active && isEditableElement(active) ? active : null;
  const focusRect = readBottom(focused);
  const shell = document.querySelector(SHELL_SELECTOR);
  const target = focusRect ?? composer;
  return {
    ih: window.innerHeight,
    vvh: viewport ? round(viewport.height) : null,
    vvTop: viewport ? round(viewport.offsetTop) : null,
    vvPageTop: viewport ? round(viewport.pageTop) : null,
    vvScale: viewport ? round(viewport.scale) : null,
    scrollTop: scroller ? round(scroller.scrollTop) : null,
    scrollH: scroller ? scroller.scrollHeight : null,
    compTop: composer?.top ?? null,
    compBottom: composer?.bottom ?? null,
    focus: focused ? describeElement(focused) : null,
    focusTop: focusRect?.top ?? null,
    focusBottom: focusRect?.bottom ?? null,
    shellH: shell ? shell.clientHeight : null,
    standalone: isStandalone(),
    inputCovered: computeInputCovered(target?.bottom ?? null, viewport),
  };
}

// ---------------------------------------------------------------------------
// Flags (storage access is always guarded: it can throw or be unavailable)
// ---------------------------------------------------------------------------

export function isKeyboardDiagnosticsEnabled(): boolean {
  try {
    return window.localStorage.getItem(KBDEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Applies `?kbdebug=1|0`. Returns whether diagnostics are enabled afterwards.
 * Writes nothing when the param is absent, or when disabling an already
 * disabled diagnostic.
 */
export function applyKeyboardDiagnosticsParam(search: string = window.location.search): boolean {
  let param: string | null = null;
  try {
    param = new URLSearchParams(search).get(KBDEBUG_PARAM);
  } catch {
    param = null;
  }
  try {
    if (param === '1') {
      window.localStorage.setItem(KBDEBUG_STORAGE_KEY, '1');
    } else if (param === '0' && window.localStorage.getItem(KBDEBUG_STORAGE_KEY) !== null) {
      window.localStorage.removeItem(KBDEBUG_STORAGE_KEY);
      window.localStorage.removeItem(KBDEBUG_LOG_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: nothing to persist.
  }
  return isKeyboardDiagnosticsEnabled();
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

export interface KeyboardDiagnosticsController {
  getEntries(): readonly LogEntry[];
  /** The newest snapshot (taken on demand when nothing was recorded yet). */
  getLatest(): Snapshot;
  subscribe(listener: () => void): () => void;
  clear(): void;
  /** Writes the log to storage now. */
  flush(): void;
  /** Records a marker entry (used by the panel for manual notes). */
  mark(kind: string, detail?: string): void;
  stop(): void;
  modes(): { stored: KeyboardMode; active: KeyboardMode };
}

let activeController: KeyboardDiagnosticsController | null = null;

function loadStoredEntries(): LogEntry[] {
  try {
    const raw = window.localStorage.getItem(KBDEBUG_LOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as LogEntry[])
      .filter((e) => typeof e.t === 'number' && typeof e.kind === 'string')
      .slice(-MAX_ENTRIES);
  } catch {
    return [];
  }
}

/**
 * Starts recording. Returns null (and touches nothing) unless the flag is
 * set. Idempotent: a second call returns the running controller.
 */
export function startKeyboardDiagnostics(): KeyboardDiagnosticsController | null {
  if (activeController) return activeController;
  if (!isKeyboardDiagnosticsEnabled()) return null;

  const entries: LogEntry[] = loadStoredEntries();
  const listeners = new Set<() => void>();
  let lastCovered: boolean | null = null;
  let lastPersistAt = 0;
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  const lastFiredAt = new Map<string, number>();

  let observedComposer: Element | null = null;
  let observedMain: Element | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;

  const notify = () => {
    listeners.forEach((fn) => {
      fn();
    });
  };

  const persist = () => {
    if (persistTimer !== null) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    lastPersistAt = Date.now();
    try {
      window.localStorage.setItem(KBDEBUG_LOG_STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Quota or unavailable storage: keep logging in memory.
    }
  };

  const schedulePersist = () => {
    if (persistTimer !== null) return;
    const wait = lastPersistAt + PERSIST_INTERVAL_MS - Date.now();
    if (wait <= 0) {
      persist();
    } else {
      persistTimer = setTimeout(persist, wait);
    }
  };

  const record = (kind: string, detail?: string) => {
    if (stopped) return;
    const snap = takeSnapshot();
    const entry: LogEntry = { t: Date.now(), kind, snap };
    if (detail !== undefined) entry.detail = detail;
    if (lastCovered !== null && lastCovered !== snap.inputCovered) {
      entry.flip = snap.inputCovered ? 'covered' : 'uncovered';
      entry.prev = entries.slice(-PREV_KINDS).map((e) => e.kind);
    }
    lastCovered = snap.inputCovered;
    entries.push(entry);
    if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
    schedulePersist();
    notify();
  };

  /** Scroll style events can fire every frame: keep at most one per window. */
  const recordCoalesced = (kind: string) => {
    const now = Date.now();
    const last = lastFiredAt.get(kind) ?? 0;
    if (now - last < SCROLL_COALESCE_MS) return;
    lastFiredAt.set(kind, now);
    record(kind);
  };

  // --- event listeners ------------------------------------------------------
  const viewport = window.visualViewport;
  const onVvResize = () => {
    record('vv:resize');
  };
  const onVvScroll = () => {
    recordCoalesced('vv:scroll');
  };
  const onWinResize = () => {
    record('win:resize');
  };
  const onWinScroll = () => {
    recordCoalesced('win:scroll');
  };
  const onOrientation = () => {
    record('orientationchange');
  };
  const onVisibility = () => {
    record('visibility', document.visibilityState);
    if (document.visibilityState === 'hidden') persist();
  };
  const onFocusIn = (event: Event) => {
    if (event.target instanceof Element && isEditableElement(event.target)) {
      record('focusin', describeElement(event.target));
    }
  };
  const onFocusOut = (event: Event) => {
    if (event.target instanceof Element && isEditableElement(event.target)) {
      record('focusout', describeElement(event.target));
    }
  };
  const onPageHide = () => {
    persist();
  };

  viewport?.addEventListener('resize', onVvResize);
  viewport?.addEventListener('scroll', onVvScroll);
  window.addEventListener('resize', onWinResize);
  window.addEventListener('scroll', onWinScroll, { passive: true });
  window.addEventListener('orientationchange', onOrientation);
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);

  // --- observers on the composer and main ----------------------------------
  const sizeText = (target: Element): string => {
    const rect = target.getBoundingClientRect();
    return `${String(Math.round(rect.width))}x${String(Math.round(rect.height))}`;
  };

  const onResizeObserved = (observed: ResizeObserverEntry[]) => {
    for (const item of observed) {
      const which = item.target === observedComposer ? 'composer' : 'main';
      record(`ro:${which}`, sizeText(item.target));
    }
  };

  const onMutations = (records: MutationRecord[]) => {
    for (const mutation of records) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) record('sibling+', describeElement(node));
      });
      mutation.removedNodes.forEach((node) => {
        if (node instanceof Element) record('sibling-', describeElement(node));
      });
    }
  };

  /** (Re)attach observers when the composer or main element was replaced. */
  const ensureObservers = () => {
    if (stopped) return;
    const composer = document.querySelector(COMPOSER_SELECTOR);
    const main = document.querySelector('main');
    if (composer === observedComposer && main === observedMain) return;

    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    observedComposer = composer;
    observedMain = main;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onResizeObserved);
      if (composer) resizeObserver.observe(composer);
      if (main) resizeObserver.observe(main);
    }
    if (typeof MutationObserver !== 'undefined' && composer) {
      // Direct children of every ancestor up to and including <main>: rows
      // that appear or disappear above the composer show up here. childList
      // only (no subtree), so message text churn is not observed.
      mutationObserver = new MutationObserver(onMutations);
      let node: Element | null = composer.parentElement;
      while (node) {
        mutationObserver.observe(node, { childList: true });
        if (node === main) break;
        node = node.parentElement;
      }
    }
  };

  const rescanTimer = setInterval(ensureObservers, OBSERVER_RESCAN_MS);

  const controller: KeyboardDiagnosticsController = {
    getEntries: () => entries,
    getLatest: () => entries[entries.length - 1]?.snap ?? takeSnapshot(),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    clear() {
      entries.length = 0;
      lastCovered = null;
      persist();
      notify();
    },
    flush: persist,
    mark(kind, detail) {
      record(kind, detail);
    },
    modes: () => ({ stored: getStoredKeyboardMode(), active: getActiveKeyboardMode() }),
    stop() {
      if (stopped) return;
      persist();
      stopped = true;
      viewport?.removeEventListener('resize', onVvResize);
      viewport?.removeEventListener('scroll', onVvScroll);
      window.removeEventListener('resize', onWinResize);
      window.removeEventListener('scroll', onWinScroll);
      window.removeEventListener('orientationchange', onOrientation);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      clearInterval(rescanTimer);
      if (persistTimer !== null) clearTimeout(persistTimer);
      listeners.clear();
      activeController = null;
    },
  };
  activeController = controller;

  ensureObservers();
  record('start', `mode=${getActiveKeyboardMode()}`);
  return controller;
}

/** One line per entry for the panel and the copied log. */
export function formatEntry(entry: LogEntry): string {
  const d = new Date(entry.t);
  const pad = (n: number, width = 2) => String(n).padStart(width, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
  const s = entry.snap;
  const parts = [
    time,
    entry.kind + (entry.detail ? ` ${entry.detail}` : ''),
    `ih=${String(s.ih)}`,
    `vv=${String(s.vvh)}/${String(s.vvTop)}`,
    `sc=${String(s.scrollTop)}/${String(s.scrollH)}`,
    `comp=${String(s.compTop)}-${String(s.compBottom)}`,
    `foc=${s.focus ?? '-'}:${String(s.focusTop)}-${String(s.focusBottom)}`,
    `shell=${String(s.shellH)}`,
    `cov=${s.inputCovered ? '1' : '0'}`,
  ];
  let line = parts.join(' ');
  if (entry.flip) {
    line += ` <<${entry.flip === 'covered' ? 'COVERED' : 'UNCOVERED'}>> after ${(entry.prev ?? []).join(', ')}`;
  }
  return line;
}

/** Text placed on the clipboard: a header line, then every entry. */
export function buildCopyText(entries: readonly LogEntry[]): string {
  const snap = takeSnapshot();
  const modes = {
    stored: getStoredKeyboardMode(),
    active: getActiveKeyboardMode(),
  };
  const header = `kbdebug ua=${navigator.userAgent} standalone=${snap.standalone ? 'yes' : 'no'} kbmode=${modes.active}(flag=${modes.stored}) entries=${String(entries.length)}`;
  return [header, ...entries.map(formatEntry)].join('\n');
}
